import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/uptime-kuma')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Uptime Kuma Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Uptime Kuma: a free self-hosted monitor vs a hosted service. Compare setup, upkeep, alerts, team access, and API." },
      { property: 'og:title', content: 'Uptime Kuma Alternative — Uptrack' },
      { property: 'og:description', content: "Uptime Kuma is free, MIT-licensed, and self-hosted. Uptrack is hosted, with alert confirmation, team seats, and a REST API." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-uptime-kuma.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/uptime-kuma' },
    ],
    scripts: comparisonScripts('uptime-kuma', "Uptime Kuma"),
  }),
})

const data: ComparisonData = {
  competitor: "Uptime Kuma",
  tagline: "Uptime Kuma is a popular open-source, self-hosted monitor with more than 90 notification services. Uptrack is a hosted alternative for teams that do not want to run their own monitoring server.",
  heroDescription: "These facts come from the Uptime Kuma README, issues, and wiki on GitHub as of October 2026, and from Uptrack's pricing page.",

  features: [
    { feature: "Pricing", uptrack: "Free plan (5 monitors); Pro from $3/mo", competitor: "Free and open source (MIT)" },
    { feature: "Infrastructure required", uptrack: "None (hosted)", competitor: "Your own server", uptrackWins: true },
    { feature: "Fastest check interval", uptrack: "30 sec", competitor: "20 sec" },
    { feature: "Alert confirmation", uptrack: "2-5 consecutive failed checks; several check locations must confirm", competitor: "Configurable retries" },
    { feature: "Check locations", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "Wherever you host it" },
    { feature: "Notification channels", uptrack: "Email, Slack, Discord, Telegram, webhooks", competitor: "90+ services" },
    { feature: "Check types", uptrack: "HTTP, keyword, TCP, ping, DNS, SSL, domain expiry, heartbeat, browser (Playwright)", competitor: "HTTP, TCP, Ping, DNS, Docker, Steam, MQTT, and more" },
    { feature: "Status pages", uptrack: "Included", competitor: "Multiple, with domain mapping" },
    { feature: "Maintenance windows", uptrack: "Team and Business", competitor: "Built-in" },
    { feature: "REST API", uptrack: "Full REST API", competitor: "Limited (badges, metrics); management uses Socket.IO", uptrackWins: true },
    { feature: "Team accounts", uptrack: "Multi-user with roles", competitor: "Single user", uptrackWins: true },
    { feature: "Data ownership", uptrack: "Hosted by Uptrack", competitor: "Fully self-hosted (your data)" },
    { feature: "Docker container monitoring", uptrack: "Not available", competitor: "Native Docker integration" },
  ],

  painPoints: [
    {
      title: "You keep it running",
      description: "Uptime Kuma monitors your services, but you monitor Uptime Kuma. If its server goes down, your monitoring goes with it. You manage OS updates, Docker upgrades, disk space, backups, and TLS.",
    },
    {
      title: "Single-user accounts",
      description: "Uptime Kuma has one login per instance. Multi-user support is an open feature request (issue #128) that maintainers plan for later.",
    },
    {
      title: "API is Socket.IO-based",
      description: "Uptime Kuma exposes REST endpoints for badges, metrics, and status page data. Managing monitors goes through its Socket.IO API with a username and password, which is harder to use from CI/CD or scripts.",
    },
  ],

  differentiators: [
    "Nothing to host: no servers, containers, or backups to maintain. Uptrack runs on redundant, multi-node infrastructure.",
    "Alert confirmation: 2-5 consecutive failed checks before alerting; several check locations must confirm.",
    "Full REST API for CI/CD and infrastructure-as-code, plus an MCP server for AI tools such as Claude.",
    "Team members with roles and permissions, included in every paid plan.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
  ],

  notForYou: [
    "If you want free monitoring with no plan limits, Uptime Kuma is MIT-licensed and costs nothing beyond your server.",
    "If you need 90+ notification services (Telegram, Discord, Gotify, ntfy, Apprise, and more), Uptime Kuma supports far more channels than Uptrack.",
    "If you need Docker container monitoring (checking if containers are running), Uptime Kuma has native Docker integration. Uptrack doesn't.",
    "If you need full data ownership, self-hosting gives you complete control over where your monitoring data lives.",
    "If you have DevOps capacity and enjoy running your own infrastructure, Uptime Kuma is an excellent choice. Self-hosting is a valid approach.",
    "If you need game server monitoring (Steam, Minecraft), Uptime Kuma has specialized check types for game servers.",
    "If you need checks faster than 30 seconds, Uptime Kuma can check every 20 seconds.",
  ],
}
